import React, { Component, createRef } from "react";
import { t } from "../../../components/CustomComponent.js";
import ICON from "../icons.js";

/* ============================================================================
 * HomeTransit — "ค้นหาตามแนวรถไฟฟ้า"
 *
 * คนกรุงเทพฯ เลือกที่อยู่จากสายรถไฟฟ้าก่อนเลือกเขต จึงแยกออกมาเป็นแถบของตัวเอง
 * - เลื่อนไปทางซ้ายเรื่อย ๆ แบบวนไม่รู้จบ โดย render รายการ 2 ชุด แล้วรีเซ็ต
 *   scrollLeft เมื่อเลื่อนครบ 1 ชุด ทำให้ดูต่อเนื่องไม่มีรอยต่อ
 * - หยุดชั่วคราวเมื่อผู้ใช้ชี้เมาส์ / แตะ / เลื่อนเอง / กดปุ่มลูกศร แล้วค่อยเดินต่อ
 *   (ถ้าไม่หยุด ผู้ใช้จะกดสายที่เล็งไว้ไม่ทัน)
 * - ปิดให้อัตโนมัติถ้าผู้ใช้ตั้งค่าลดการเคลื่อนไหว (prefers-reduced-motion)
 * รายการ/สี/ลำดับ แก้ไขได้จากหลังบ้าน (/ad/home/shortcuts)
 * ========================================================================== */
const SPEED = 0.4;         // พิกเซลต่อเฟรม (~24px/วินาที) ช้าพอให้อ่านชื่อสายทัน
const RESUME_DELAY = 2500; // เวลาที่หยุดหลังผู้ใช้เลื่อนเอง (มิลลิวินาที)

export default class HomeTransit extends Component {
  track = createRef();
  paused = false;
  offset = 0; // เก็บระยะเลื่อนสะสมเอง เพราะ scrollLeft ปัดเป็นจำนวนเต็ม
              // ถ้าบวกทีละ 0.4 ใส่ scrollLeft ตรง ๆ จะถูกปัดเหลือ 0 ทุกเฟรม (ไม่ขยับเลย)

  componentDidMount() {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!reduce) this.raf = requestAnimationFrame(this.step);
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.resume_timer);
  }

  step = () => {
    const el = this.track.current;
    if (el) {
      if (this.paused) {
        // ระหว่างหยุด ผู้ใช้อาจเลื่อนเอง จึงจำตำแหน่งล่าสุดไว้เดินต่อจากจุดนั้น
        this.offset = el.scrollLeft;
      } else {
        const half = el.scrollWidth / 2; // ความกว้างของรายการ 1 ชุด (render ไว้ 2 ชุด)
        this.offset += SPEED;
        if (this.offset >= half) this.offset -= half;
        el.scrollLeft = this.offset;
      }
    }
    this.raf = requestAnimationFrame(this.step);
  };

  pause = () => {
    clearTimeout(this.resume_timer);
    this.paused = true;
  };

  resume = () => {
    clearTimeout(this.resume_timer);
    this.paused = false;
  };

  // หยุดชั่วคราวแล้วเดินต่อเอง ใช้กับการกดลูกศร/ปัดนิ้ว ที่ไม่มีจังหวะ "เลิกชี้"
  pauseAwhile = () => {
    clearTimeout(this.resume_timer);
    this.paused = true;
    this.resume_timer = setTimeout(() => { this.paused = false; }, RESUME_DELAY);
  };

  scroll = (direction) => {
    const el = this.track.current;
    if (!el) return;
    this.pauseAwhile();
    const step = Math.max(240, Math.round(el.clientWidth * 0.7));
    el.scrollBy({ left: step * direction, behavior: "smooth" });
  };

  line = (item, copy) => {
    const { onSelect } = this.props;

    return (
      <button
        key={`${item.uid}-${copy}`}
        type="button"
        className="hm-line"
        aria-hidden={copy === 1 ? "true" : undefined}
        onClick={() => onSelect && onSelect(item)}
      >
        <span className="hm-line__logo hm-ico" style={{ background: item.color }}>
          {item.icon}
        </span>
        <span className="hm-line__body">
          <span className="hm-line__name">{item.name}</span>
          <span className="hm-line__sub">{item.sub}</span>
        </span>
        <span className="hm-line__more">
          {t("view_all")}
          <span className="hm-ico">{ICON.chevron_right_small}</span>
        </span>
      </button>
    );
  };

  render() {
    const list = this.props.data?.items || [];
    if (!list.length) return null;

    return (
      <section className="hm-section" id="hm-transit">
        <div className="hm-container">
          <div className="hm-section__head hm-section__head--tight">
            <h2 className="hm-section__title hm-section__title--sm">{this.props.data?.section?.title || ""}</h2>
          </div>

          <div
            className="hm-carousel hm-carousel--line"
            onMouseEnter={this.pause}
            onMouseLeave={this.resume}
            onFocus={this.pause}
            onBlur={this.resume}
          >
            <button type="button" className="hm-carousel__btn is-prev" aria-label="prev" onClick={() => this.scroll(-1)}>
              <span className="hm-ico">{ICON.chevron_left}</span>
            </button>

            <div
              className="hm-carousel__track is-auto"
              ref={this.track}
              onTouchStart={this.pauseAwhile}
              onWheel={this.pauseAwhile}
            >
              {/* ชุดที่ 2 เป็นสำเนาสำหรับต่อภาพให้วนได้ไม่มีรอยต่อ */}
              {[0, 1].map((copy) => list.map((item) => this.line(item, copy)))}
            </div>

            <button type="button" className="hm-carousel__btn is-next" aria-label="next" onClick={() => this.scroll(1)}>
              <span className="hm-ico">{ICON.chevron_right}</span>
            </button>
          </div>
        </div>
      </section>
    );
  }
}
